import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
};

export const useThemeTogglerRoot = () => {
  // A PREFERÊNCIA persistida é uma de três: 'light' | 'dark' | 'system'.
  // Migração: valores antigos ('light'/'dark') continuam válidos; ausência → 'light'.
  const [storedPreference, setStoredPreference] =
    useLocalStorage<ThemePreference>('theme', 'light');

  const themePreference: ThemePreference = storedPreference || 'light';

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_QUERY);

    // Mantém o valor sincronizado (ex.: SO mudou enquanto a escolha não era 'system').
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    // Só escutamos mudanças ao vivo quando a escolha é 'system'.
    if (themePreference !== 'system') {
      return;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // Tema RESOLVIDO aplicado de fato (fonte da verdade pro CSS/MUI/styled-components).
  const themeType: ResolvedTheme =
    themePreference === 'system' ? systemTheme : themePreference;

  const setTheme = useCallback(
    (theme: ThemePreference) => {
      setStoredPreference(theme);
    },
    [setStoredPreference],
  );

  return { setTheme, themeType, themePreference };
};
